import { useMemo, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { getProviderFreshness } from '@bedrock-profiles/shared';
import { useDataStore } from '../../store/dataStore';
import { Breadcrumbs } from './Breadcrumbs';
import { GlobalSearch } from '../Search/GlobalSearch';

interface TopBarProps {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
  menuButtonRef: RefObject<HTMLButtonElement | null>;
}

export function TopBar({ onMenuToggle, sidebarOpen, menuButtonRef }: TopBarProps) {
  const currentSnapshot = useDataStore((state) => state.currentSnapshot);
  // Amber whenever any provider's data is archived (older than the archive threshold).
  const hasArchivedProviders = useMemo(
    () => getProviderFreshness(currentSnapshot).some((row) => row.status === 'archived'),
    [currentSnapshot]
  );

  return (
    <header
      id="app-topbar"
      className="z-30 flex h-[68px] flex-shrink-0 items-center border-b border-slate-200/80 bg-white/95 text-slate-950 backdrop-blur"
    >
      <div className="flex h-full min-w-0 flex-shrink-0 items-center gap-1 px-2 sm:gap-2 sm:px-3 lg:w-[232px] lg:gap-3 lg:border-r lg:border-slate-200/80 lg:px-5">
        <button
          ref={menuButtonRef}
          type="button"
          data-testid="mobile-navigation-toggle"
          onClick={onMenuToggle}
          className="flex h-11 w-11 items-center justify-center rounded-md text-slate-600 transition-colors hover:bg-slate-100 lg:hidden"
          aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-controls="mobile-navigation"
          aria-expanded={sidebarOpen}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            {sidebarOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>

        <Link
          to="/"
          className="flex h-11 min-w-0 items-center gap-2 rounded-md pr-1 transition hover:bg-slate-50 lg:hover:bg-transparent lg:hover:opacity-80"
          aria-label="ModelMap home"
        >
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm shadow-blue-200">
            <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" aria-hidden="true">
              <path
                d="M12 3.2 19.2 7.3v8.4L12 19.8l-7.2-4.1V7.3L12 3.2Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
              <path d="M12 7.8 15.3 9.7v3.8L12 15.4l-3.3-1.9V9.7L12 7.8Z" fill="currentColor" />
            </svg>
          </span>
          <span className="truncate text-lg font-bold tracking-[-0.03em] text-slate-950 sm:text-xl">
            ModelMap
          </span>
        </Link>
      </div>

      <div className="flex min-w-0 flex-1 items-center justify-between gap-4 pr-2 sm:px-6 lg:px-8">
        <div className="hidden min-w-0 items-center md:flex">
          <Breadcrumbs />
        </div>

        <div className="ml-auto flex items-center gap-2.5">
          <GlobalSearch />

          <Link
            to="/status"
            data-testid="data-status-pill"
            data-archived={hasArchivedProviders ? 'true' : 'false'}
            title={hasArchivedProviders ? 'Some provider data is archived' : undefined}
            className={`hidden min-h-10 items-center gap-2 rounded-full border px-3 text-xs font-semibold transition sm:inline-flex ${
              hasArchivedProviders
                ? 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-400 hover:bg-amber-100'
                : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${hasArchivedProviders ? 'bg-amber-500' : 'bg-blue-500'}`}
              aria-hidden="true"
            />
            Data status
            {hasArchivedProviders && <span className="sr-only">: some provider data is archived</span>}
          </Link>

          <Link
            to="/changes"
            aria-label="Changes"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 hover:text-slate-950"
          >
            <svg
              className="h-[18px] w-[18px]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.7}
                d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5m6 0a3 3 0 0 1-6 0m6 0H9"
              />
            </svg>
          </Link>
        </div>
      </div>
    </header>
  );
}
